import React, { cloneElement, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Grid, Box, Button, Badge, IconButton } from '@material-ui/core'
import MuiAppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { grey } from '@material-ui/core/colors';
import MenuIcon from '@material-ui/icons/Menu';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { toggleSidebar } from 'ra-core';
import DefaultUserMenu from './UserMenu';
import { useLogout, useTranslate } from 'react-admin';
import pjson from '../../package.json'
import logo1 from '../assets/logo2.png';
import icSupport from '../assets/icons/ic_support_grey.png';
import icLogout from '../assets/icons/ic_back_login_grey.png';
import icMessages from '../assets/icons/ic_messages_grey.png';
import socketIOClient from 'socket.io-client';
import sailsIOClient from 'sails.io.js';
import { connectSocket } from '../dashboard/actions';
import DialogSupport from '../common/dialog-support';
import DialogSendSuccess from '../common/dialog-send-success';
import { increaseCountLatestVersion, changeCountLatestVersion } from '../redux/actions/tree-action';

const useStyles = makeStyles(
    theme => ({
        root: {
            [theme.breakpoints.up('sm')]: {
                height: '95px',
            },
            backgroundColor: theme.palette.background.paper,
            boxShadow: ' 25px 0 15px 0 rgba(0, 0, 0, 0.04)'
        },
        toolbar: {
            height: '100%'
        },
        menuButton: {
            marginLeft: '0.5em',
            marginRight: '0.5em',
        },
        menuButtonIconClosed: {
            transition: theme.transitions.create(['transform'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            transform: 'rotate(0deg)',
        },
        menuButtonIconOpen: {
            transition: theme.transitions.create(['transform'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            transform: 'rotate(180deg)',
        },
        title: {
            flex: 1,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        },
        iconBorder: {
            borderRightColor: grey[300],
            borderRightWidth: 1,
            borderRightStyle: 'solid'
        },
        TitleIcon: {
            fontFamily: 'UniviaPro',
            fontSize: '11px',
            fontWeight: 'normal',
            fontStretch: 'normal',
            fontStyle: 'normal',
            lineHeight: 1.36,
            letterSpacing: 'normal',
            color: "#88898c",
            paddingTop: '8px'
        },
        TitleDashboard: {
            fontFamily: 'UniviaPro',
            fontSize: '13px',
            fontWeight: 'normal',
            fontStretch: 'normal',
            fontStyle: 'normal',
            lineHeight: 1.25,
            letterSpacing: ' 0.13px',
            color: '#4f5064',
        }
    }),
    { name: 'RaAppBar' }
);

const GridContainer = ({ badge }) => {
    const classes = useStyles();
    const translate = useTranslate();
    const handleLogout = useLogout();
    const userData = localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).user.role : 0
    const history = useHistory();
    const isXSmall = useMediaQuery(theme => theme.breakpoints.down('xs'));
    const isDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
    const iconSize = isXSmall ? '16px' : '24px';
    const [openSupport, setOpenSupport] = useState(false);
    const [dialogSuccessMessage, setDialogSuccessMessage] = useState('');
    // console.log(badge);

    const handleOpenSupport = () => {
        setOpenSupport(true)
    }

    const handleCloseSupport = (status) => {
        setOpenSupport(false)
        if (status)
            setDialogSuccessMessage(translate(`pos.support.success`))
    }

    return (
        <Box width='100%'>
            <DialogSupport
                open={openSupport}
                onClose={handleCloseSupport}
            />
            <DialogSendSuccess
                open={dialogSuccessMessage.length > 0}
                onClose={() => setDialogSuccessMessage('')}
                message={dialogSuccessMessage}
            />
            <Grid container spacing={isDesktop ? 2 : 0} direction="row"
                justify="flex-end"
                alignItems="center">
                <Grid item>
                    <Box component={Button} style={{ textTransform: 'initial' }} onClick={() => history.push('/Mailbox')}>
                        <Box textAlign='center'>
                            <Badge badgeContent={badge} color="error">
                                <img alt='' src={icMessages} height={iconSize} width={iconSize} />
                            </Badge>
                            <Typography className={classes.TitleIcon}>{translate(`pos.appbar.message`)}</Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid item>
                    <Box component={Button} style={{ textTransform: 'initial' }} onClick={() => handleOpenSupport()}>
                        <Box textAlign='center'>
                            <Badge badgeContent={0}>
                                <img alt='' src={icSupport} height={iconSize} width={iconSize} />
                            </Badge>
                            <Typography className={classes.TitleIcon}>{translate(`pos.appbar.support`)}</Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid item>
                    <Box component={Button} style={{ textTransform: 'initial' }} onClick={() => handleLogout()}>
                        <Box textAlign='center'>
                            <Badge badgeContent={0}>
                                <img alt='' src={icLogout} height={iconSize} width={iconSize} />
                            </Badge>
                            <Typography className={classes.TitleIcon}>{translate(`pos.appbar.logout`)}</Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

const AppBar = props => {
    const {
        children,
        classes: classesOverride,
        className,
        logo,
        logout,
        open,
        title,
        userMenu,
        ...rest
    } = props;
    const classes = useStyles(props);
    const translate = useTranslate();
    const dispatch = useDispatch();
    const isXSmall = useMediaQuery(theme => theme.breakpoints.down('xs'));
    const isDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
    const [badge, setBadge] = useState(0);
    const [socketIO, setSocketIO] = useState(null);

    const sailsSocket = useSelector((state) => state.sailsSocket);
    const { selectedInfoPage } = useSelector((state) => state.tree);

    const updateToRedux = useCallback(
        (socket) => {
            setSocketIO(socket);
            dispatch(connectSocket(socket));
        },
        [dispatch]
    )
    let io = null;
    useEffect(() => {
        // connect to sails socket server
        if (socketIO === null && sailsSocket === null) {
            console.log(socketIO, sailsSocket);
            io = sailsIOClient(socketIOClient);
            let baseApiUrl = process.env.REACT_APP_BASE_API_URL;
            // Set the current hostname for API url if there is no one in env configuration
            if (!baseApiUrl) {
                baseApiUrl = window.location.protocol + '//' + window.location.hostname + ':1337';
            }
            io.sails.url = baseApiUrl;
            io.sails.reconnection = true;
            io.socket.on('connect', () => {
                io.socket.request({
                    method: 'POST',
                    url: '/users/subscribe',
                    headers: {
                        token: localStorage.getItem('token')
                    }
                }, function (resData) {
                    // if (resData.statusCode === 200) {
                    //   // TODO do something here
                    // } else {
                    //   // put({ type: socketActions.REGISTER_SOCKET, payload });
                    // }
                });
                io.socket.on('user', event => {
                    if (event.type === 'updateBadge') {
                        // console.log('message', event.badgeCount);
                        setBadge(event.badgeCount);
                    } else if (event.type === 'infopage') {
                        if (selectedInfoPage && selectedInfoPage.id === event.infoPageId) {
                            if (event.userId === parseInt(localStorage.getItem('id'))) {
                                dispatch(changeCountLatestVersion(0));
                            } else {
                                dispatch(increaseCountLatestVersion());
                            }
                        }
                    }
                });
            })
            if (io) {
                updateToRedux(io.socket);
            }
        } else {
            if (sailsSocket && sailsSocket.isConnected()) {
                sailsSocket.request({
                    method: 'POST',
                    url: '/users/subscribe',
                    headers: {
                        token: localStorage.getItem('token')
                    }
                }, function (resData) {
                    // if (resData.statusCode === 200) {
                    //   // TODO do something here
                    // } else {
                    //   // put({ type: socketActions.REGISTER_SOCKET, payload });
                    // }
                });
                sailsSocket.on('user', event => {
                    if (event.type === 'updateBadge') {
                        setBadge(event.badgeCount);
                    } else if (event.type === 'infopage') {
                        if (selectedInfoPage && selectedInfoPage.id === event.infoPageId) {
                            if (event.userId === parseInt(localStorage.getItem('id'))) {
                                dispatch(changeCountLatestVersion(0));
                            } else {
                                dispatch(increaseCountLatestVersion());
                            }
                        }
                    }
                });
            }
        }
        // return () => {
        //     if (socketIO) {
        //         socketIO.disconnect(true);
        //     }
        //     if (sailsSocket) {
        //         sailsSocket.disconnect(true);
        //     }
        //     updateToRedux(null);
        // }
    }, [socketIO, selectedInfoPage])
    return (
        // <HideOnScroll>
        <MuiAppBar className={classes.root} {...rest}>
            <Toolbar
                disableGutters
                variant={isXSmall ? 'regular' : 'dense'}
                className={classes.toolbar}
            >
                {
                    !isDesktop ? (
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={() => dispatch(toggleSidebar())}
                            className={classNames(classes.menuButton)}
                        >
                            <MenuIcon
                                color='primary'
                                classes={{
                                    root: open
                                        ? classes.menuButtonIconOpen
                                        : classes.menuButtonIconClosed,
                                }}
                            />
                        </IconButton>

                    ) : (

                            <Box className={classes.iconBorder} my='auto' pr={isXSmall ? '5px' : '29px'} ml='19px' mr={isXSmall ? '5px' : '29px'} display='flex' width='201px' height='47px' component='a' href='/'>
                                <img alt='' src={logo1} width='206px' height='48px' style={{ margin: 'auto 0' }} />
                            </Box>

                        )
                }
                {
                    !isXSmall && (
                        <Box width={200}>
                            <Box className={classes.TitleDashboard}>{translate(`pos.user.title`)}</Box>
                            {
                                !isXSmall && <Typography
                                    className={classes.TitleIcon}>
                                    {
                                        translate(`pos.appbar.version`) + " " + pjson.version
                                        + (process.env.REACT_APP_BUILD_NUMBER ? `.${process.env.REACT_APP_BUILD_NUMBER}` : '')
                                    }
                                </Typography>
                            }
                        </Box>
                    )
                }
                <Box display='flex' width="100%" justifyContent='flex-end'>
                    <GridContainer badge={badge} />
                    {cloneElement(userMenu, { logout })}
                </Box>


            </Toolbar>
        </MuiAppBar>
        // </HideOnScroll>
    );
};

AppBar.propTypes = {
    children: PropTypes.node,
    classes: PropTypes.object,
    className: PropTypes.string,
    logout: PropTypes.element,
    open: PropTypes.bool,
    userMenu: PropTypes.element,
};

AppBar.defaultProps = {
    userMenu: <DefaultUserMenu />,
};

export default AppBar;